import React, { useEffect, useRef } from 'react';
import {
    Drawer, Box, Typography, IconButton, useMediaQuery, useTheme, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KitchenIcon from '@mui/icons-material/Kitchen';
import { IPedido } from '../../../../../interface/IPedido';
import { MiniKDSContent } from '../MiniKDSContent';
import { useKDSPorPedido } from '../../../../../../cozinha/hooks/useKDSPorPedido';

interface MiniKDSDrawerProps {
    open: boolean;
    pedido: IPedido | null;
    onClose: () => void;
    onTemItens: () => void;
}

const DRAWER_WIDTH = 440;

const MiniKDSChecker: React.FC<{ pedidoUuid: string; onTemItens: () => void }> = ({ pedidoUuid, onTemItens }) => {
    const { loading, temItensAtivos } = useKDSPorPedido(pedidoUuid);
    const notificouRef = useRef(false);

    useEffect(() => {
        if (!loading && temItensAtivos && !notificouRef.current) {
            notificouRef.current = true;
            onTemItens();
        }
    }, [loading, temItensAtivos]);

    return null;
};

export const MiniKDSDrawer: React.FC<MiniKDSDrawerProps> = ({ open, pedido, onClose, onTemItens }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {pedido && !open && (
                <MiniKDSChecker key={pedido.uuid} pedidoUuid={pedido.uuid} onTemItens={onTemItens} />
            )}

            <Drawer
                anchor="right"
                variant="temporary"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true,
                    hideBackdrop: true,
                    disableScrollLock: true,
                    disableEnforceFocus: true,
                }}
                sx={{
                    zIndex: 100000,
                    pointerEvents: 'none',
                    '& .MuiDrawer-paper': {
                        pointerEvents: 'auto',
                        width: isMobile ? '100vw' : DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        height: '100%',
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                }}>
                    <KitchenIcon fontSize="small" color="action" />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            KDS — Pedido #{pedido?.numeroPedido ?? pedido?.numeroPedidoDiario ?? '—'}
                        </Typography>
                        {pedido?.cliente?.nome && (
                            <Typography variant="caption" color="text.secondary">
                                {pedido.cliente.nome}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Divider />

                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {open && pedido && (
                        <MiniKDSContent key={pedido.uuid} pedido={pedido} />
                    )}
                </Box>
            </Drawer>
        </>
    );
};
